export const brand = {
  name: 'Atlas Visual de Terapia Intravenosa para Enfermería',
  promise: 'Domina la Terapia Intravenosa con un sistema visual',
  promiseSub: '6 Tomos Visuales de Terapia Intravenosa',
  promiseAccent: 'para Enfermería',
  subtitle:
    'Consulta en segundos las principales soluciones IV y encuentra composición, tonicidad, electrolitos, precauciones y cuidados de enfermería organizados en un solo lugar.',
  mockupCaption: 'Atlas Visual de Terapia Intravenosa para Enfermería: 6 Tomos + 5 Bonos Profesionales',
}

export const heroBadges: string[] = ['Acceso inmediato', 'PDF imprimible', 'Acceso de por vida']

export interface Stat {
  value: string
  label: string
}

export const stats: Stat[] = [
  { value: '6', label: 'tomos visuales' },
  { value: '+290', label: 'páginas de contenido' },
  { value: '10+', label: 'áreas esenciales de terapia IV' },
  { value: '5', label: 'bonos profesionales' },
]

export const proofNote =
  'Un sistema visual de estudio y consulta para estudiantes y profesionales de Enfermería'

export const universities: string[] = [
  'Soluciones IV',
  'Tonicidad',
  'Electrolitos',
  'Osmolaridad',
  'Cuidados',
  'Seguridad',
]

export interface PainGroup {
  audience: string
  points: string[]
}

export const painGroups: PainGroup[] = [
  {
    audience: 'Si eres estudiante de Enfermería',
    points: [
      'Estudias soluciones IV, pero después confundes cuál es isotónica, hipotónica o hipertónica',
      'Entiendes la teoría, pero te cuesta relacionar composición, tonicidad y osmolaridad',
      'Tienes apuntes, capturas y PDFs dispersos y pierdes tiempo buscando información',
      'Memorizas Na⁺, K⁺, Cl⁻ o mOsm/L sin terminar de comprender qué significan',
    ],
  },
  {
    audience: 'Si ya trabajas en Enfermería',
    points: [
      'Quieres repasar rápidamente composiciones, diferencias y precauciones',
      'No quieres depender únicamente de la memoria',
      'Necesitas tener soluciones, electrolitos y cuidados organizados en un solo lugar',
      'Quieres consultar el material desde el celular, tablet o tenerlo impreso',
    ],
  },
]

export const solutionPoints: string[] = [
  '6 tomos especializados: desde fundamentos hasta soluciones, electrolitos y cuidados.',
  '+290 páginas: láminas, comparativas, tablas y referencias visuales.',
  'Soluciones comparadas: diferencia isotónicas, hipotónicas, hipertónicas y glucosadas.',
  'Electrolitos identificados: Na⁺, K⁺, Cl⁻, Ca²⁺, Mg²⁺ y más.',
  'Consulta visual: encuentra composición, tonicidad y precauciones rápidamente.',
  '5 bonos profesionales: cálculos, compatibilidad, reacciones, acceso venoso y seguridad.',
]

export interface Module {
  number: string
  title: string
  count: string
  unit: string
  summary: string
  closing: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de Terapia Intravenosa',
    count: '1',
    unit: 'Tomo',
    summary:
      'Comprende líquidos corporales, electrolitos, ósmosis, osmolaridad, tonicidad, cristaloides, coloides y accesos vasculares.',
    closing: 'La base para entender todo lo que viene después.',
  },
  {
    number: '02',
    title: 'Soluciones Isotónicas',
    count: '2',
    unit: 'Tomo',
    summary:
      'Cloruro de Sodio 0,9%, Ringer Lactato, Hartmann y soluciones balanceadas comparadas visualmente. Incluye: composición · electrolitos · osmolaridad · diferencias clave.',
    closing: 'Misma categoría no significa misma composición.',
  },
  {
    number: '03',
    title: 'Soluciones Hipotónicas e Hipertónicas',
    count: '3',
    unit: 'Tomo',
    summary:
      'NaCl 0,45%, 3% y 5% explicadas mediante movimiento de agua, concentración de sodio y osmolaridad.',
    closing: 'Comprende qué cambia cuando aumenta o disminuye la concentración.',
  },
  {
    number: '04',
    title: 'Soluciones Glucosadas y Combinadas',
    count: '4',
    unit: 'Tomo',
    summary:
      'Dextrosa 5%, Dextrosa 10% y sus principales combinaciones con NaCl y Ringer Lactato. Incluye: glucosa · electrolitos · osmolaridad · aporte energético.',
    closing: 'Aprende qué hay realmente detrás de cada nombre y porcentaje.',
  },
  {
    number: '05',
    title: 'Electrolitos y Soluciones Especiales',
    count: '5',
    unit: 'Tomo',
    summary:
      'Potasio, magnesio, calcio, fosfato, bicarbonato, manitol y albúmina organizados visualmente. Incluye: mEq · mmol · concentraciones · diferencias clave.',
    closing: 'Comprende qué aporta cada preparación y qué la diferencia.',
  },
  {
    number: '06',
    title: 'Cuidados de Enfermería en Terapia Intravenosa',
    count: '6',
    unit: 'Tomo',
    summary:
      'Principales controles y cuidados antes, durante y después de una terapia IV. Incluye: accesos · balance hídrico · infiltración · extravasación · flebitis · infección · monitorización.',
    closing: 'Aprende qué observar y qué señales no debes ignorar.',
  },
]

export interface GalleryItem {
  title: string
  caption: string
  image: string
}

export const gallery: GalleryItem[] = [
  {
    title: 'Misma categoría. Diferente composición.',
    caption:
      'Cloruro de Sodio 0,9%, Ringer Lactato/Hartmann y solución balanceada multielectrolítica comparados electrolito por electrolito.',
    image:
      'https://cdn.magicpatterns.com/uploads/uBNnzWKoTyn8knCzo9eTT3/Captura_de_pantalla_2026-09-30_a_la(s)_8.36.58_p._m..png',
  },
  {
    title: 'Antes de memorizarla, aprende a leerla',
    caption:
      'Cinco preguntas para diferenciar visualmente las principales soluciones isotónicas: sodio, cloruro, otros electrolitos, osmolaridad y aniones.',
    image:
      'https://cdn.magicpatterns.com/uploads/b19WT9cksKnm4AYtTFF3Re/Captura_de_pantalla_2026-09-30_a_la(s)_8.37.14_p._m..png',
  },
  {
    title: 'Aprende a leer la etiqueta',
    caption:
      'Nombre, concentración, composición, electrolitos, osmolaridad, vía de administración, lote, vencimiento y advertencias identificados sobre la bolsa.',
    image:
      'https://cdn.magicpatterns.com/uploads/gL2iBtmDqp6nHhtLeojRyV/Captura_de_pantalla_2026-09-30_a_la(s)_8.37.40_p._m..png',
  },
  {
    title: 'Lactato ≠ ácido láctico en una bolsa',
    caption:
      'Del lactato al metabolismo y al efecto alcalinizante indirecto, con los conceptos que no debes confundir.',
    image:
      'https://cdn.magicpatterns.com/uploads/sMRpdXofKLAyGEFHcttwTo/Captura_de_pantalla_2026-09-30_a_la(s)_8.38.02_p._m..png',
  },
  {
    title: 'Dos aniones que cambian el perfil',
    caption:
      'Acetato y gluconato: cómo contribuyen al perfil electrolítico de las soluciones balanceadas frente al NaCl 0,9%.',
    image:
      'https://cdn.magicpatterns.com/uploads/iCV8tB4vpigmsuZogVKwuL/Captura_de_pantalla_2026-09-30_a_la(s)_8.38.33_p._m..png',
  },
  {
    title: 'Multielectrolítica balanceada',
    caption:
      'Formulación tipo Plasma-Lyte A: Na⁺, K⁺, Mg²⁺, Cl⁻, acetato, gluconato y osmolaridad ≈294 mOsm/L en una sola ficha.',
    image:
      'https://cdn.magicpatterns.com/uploads/3qZ4onphQmohz8iiGTvGjp/Captura_de_pantalla_2026-09-30_a_la(s)_8.38.49_p._m..png',
  },
  {
    title: 'Los electrolitos que no debes pasar por alto',
    caption:
      'Tabla comparativa de NaCl 0,9%, Ringer Lactato y balanceada tipo Plasma-Lyte A, en mEq/L.',
    image:
      'https://cdn.magicpatterns.com/uploads/hTY2TJx3EVeGN2rAd6dzYm/Captura_de_pantalla_2026-09-30_a_la(s)_8.39.03_p._m..png',
  },
]

export interface Bonus {
  tag: string
  title: string
  subtitle?: string
  value: string
  benefit: string
  image: string
}

export const bonusesTotalValue = '$30.450'

export const bonuses: Bonus[] = [
  {
    tag: 'Bono 01',
    title: 'Guía Práctica de Cálculo de Goteo e Infusión',
    value: '$5.990',
    benefit:
      'Gotas por minuto, microgotas, macrogoteo, mL/h, volumen, tiempo y conversiones explicados visualmente.',
    image: 'https://cdn.magicpatterns.com/uploads/utzQ2iKnLXiCgwMQaMLZjy/image.png',
  },
  {
    tag: 'Bono 02',
    title: 'Guía Visual de Compatibilidad IV',
    value: '$6.990',
    benefit:
      'Principios de compatibilidad, precipitación, pH, electrolitos y factores que deben verificarse antes de combinar productos.',
    image: 'https://cdn.magicpatterns.com/uploads/h3B5XJWBY6GXz4aXCLCzqq/image.png',
  },
  {
    tag: 'Bono 03',
    title: 'Manual Visual de Reacciones Adversas y Señales de Alerta',
    value: '$5.990',
    benefit:
      'Infiltración, extravasación, flebitis, reacciones locales y principales señales que requieren valoración.',
    image: 'https://cdn.magicpatterns.com/uploads/k5xnUxd4JbtAVvTxN2N1Za/image.png',
  },
  {
    tag: 'Bono 04',
    title: 'Atlas de Acceso Venoso Periférico',
    value: '$6.990',
    benefit:
      'Regiones de acceso, catéteres, calibres, fijación, vigilancia y diferencias entre periférico, midline y PICC.',
    image: 'https://cdn.magicpatterns.com/uploads/vkuQjk8qTHfB9bQrmcX3k3/mockup-bono4.png',
  },
  {
    tag: 'Bono 05',
    title: 'Checklist de Seguridad en Terapia Intravenosa',
    value: '$4.490',
    benefit:
      'Una referencia rápida para revisar los puntos esenciales antes, durante y después de una terapia IV.',
    image: 'https://cdn.magicpatterns.com/uploads/hi2obwWc8NchXiGKJdgb4A/image.png',
  },
]

export interface Testimonial {
  initials: string
  quote: string
  author: string
  role: string
  city: string
}

// Testimonios de ejemplo (placeholders editables). Reemplázalos por testimonios reales.
export const testimonials: Testimonial[] = [
  {
    initials: 'EE',
    quote:
      'Antes tenía las soluciones separadas entre apuntes y tablas. Verlas comparadas visualmente me ayudó a entender mucho mejor sus diferencias.',
    author: 'Camila R.',
    role: 'Estudiante de Enfermería',
    city: 'Córdoba, Argentina',
  },
  {
    initials: 'TE',
    quote:
      'Lo que más me gustó fue tener toda la información organizada por temas. Encuentro mucho más rápido lo que quiero repasar.',
    author: 'Valentina M.',
    role: 'Técnica en Enfermería',
    city: 'Mendoza, Argentina',
  },
  {
    initials: 'PE',
    quote:
      'Las composiciones, electrolitos y precauciones quedan mucho más claras cuando están presentadas visualmente.',
    author: 'Andrés P.',
    role: 'Profesional de Enfermería',
    city: 'Rosario, Argentina',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Cuándo recibo el material?',
    answer: 'El acceso es inmediato después de confirmar el pago.',
  },
  {
    question: '¿Sirve si soy estudiante de Enfermería?',
    answer: 'Sí. Comienza desde los fundamentos y avanza hacia soluciones, electrolitos y cuidados.',
  },
  {
    question: '¿Sirve si ya trabajo en Enfermería?',
    answer: 'Sí. Puede utilizarse como material educativo de repaso y consulta visual.',
  },
  {
    question: '¿Qué incluye?',
    answer: '6 Tomos + 5 Bonos Profesionales.',
  },
  {
    question: '¿Puedo verlo desde el celular?',
    answer: 'Sí. Recibes archivos digitales en PDF compatibles con celular, tablet y computadora.',
  },
  {
    question: '¿Se puede imprimir?',
    answer: 'Sí. El material está diseñado en formato A4.',
  },
  {
    question: '¿El acceso es de por vida?',
    answer: 'Sí. Es un pago único, sin suscripción.',
  },
  {
    question: '¿Es un curso?',
    answer: 'No. Es un sistema visual en PDF.',
  },
  {
    question: '¿Incluye cálculos de goteo?',
    answer: 'Sí. Están incluidos en uno de los bonos.',
  },
  {
    question: '¿Incluye compatibilidad IV?',
    answer: 'Sí. Incluye una guía educativa sobre principios y precauciones de compatibilidad.',
  },
  {
    question: '¿Me indica qué solución utilizar con cada paciente?',
    answer:
      'No. La elección depende de la valoración clínica, prescripción y protocolos correspondientes.',
  },
  {
    question: '¿Reemplaza los protocolos hospitalarios?',
    answer: 'No. Es material educativo y de consulta visual.',
  },
]

export const checkoutUrl = 'https://enfermeria.impultienda.ar'

export interface Offer {
  name: string
  listPrice: string
  totalValue: string
  price: string
  priceValue: number
  currency: string
  cta: string
}

export const offer: Offer = {
  name: 'Atlas Visual de Terapia Intravenosa para Enfermería — Sistema Completo',
  listPrice: '$34.990',
  totalValue: '$65.440',
  price: '$14.990',
  priceValue: 14990,
  currency: 'ARS',
  cta: 'Quiero el Atlas + 5 Bonos',
}

export const pricingRows: { label: string; value: string }[] = [
  {
    label: 'Atlas Visual de Terapia Intravenosa para Enfermería (6 Tomos · +290 páginas)',
    value: '$34.990',
  },
  { label: '5 bonos profesionales incluidos', value: '$30.450' },
]

export const footerLinks: { label: string; href: string }[] = [
  { label: 'Términos de servicio', href: '#planes' },
  { label: 'Política de privacidad', href: '#planes' },
  { label: 'Política de reembolso', href: '#planes' },
  { label: 'Contacto', href: '#planes' },
]

export const heroImage = 'https://cdn.magicpatterns.com/uploads/bnEKGCg81gD1iPKxYDt4rm/mockup-principal.png'

